import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, ShieldCheck, Settings2, Bell, Sparkles, Megaphone, Eye, EyeOff, Sun, Moon, X, Rocket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { toast } from 'sonner';
import { deriveBrandColors } from '../../utils/brandColors';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import { useThemeMode } from '../../hooks/useThemeMode';
import { useCachedImage } from '../../hooks/useCachedImage';
import { useDismissibleFeed } from '../../hooks/useDismissibleFeed';
import { DemoSandboxModal } from '../DemoSandboxModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BRANDING = {
  brand_logo_url: '',
  brand_logo_dark_url: '',
  login_title_text: 'RupiWise',
  login_subtitle_text: 'Welcome back',
  feature_1_title: 'Smart and Affordable',
  feature_1_desc: 'Optimize your cash flow effortlessly.',
  feature_2_title: 'Cloud Based',
  feature_2_desc: 'Your data is protected by industry-leading security.',
  feature_3_title: 'Simple and Robust',
  feature_3_desc: 'Powerful features, intuitive control.',
  background_image_url: '',
  background_image_dark_url: '',
  background_opacity: 30,
  background_opacity_dark: 30,
  // SuperAdmin-configured paired sentinels — mirror the canonical fallbacks
  // from ThemeContext so the AuthPage matches before the public site-config
  // fetch resolves.
  theme_default_light: '#1a1a2e',
  theme_default_dark: '#f59e0b',
  demo_button_enabled: true,
  // Login-page left-panel hero — SuperAdmin can swap these from the
  // Branding page; blank means fall back to the shipped defaults in
  // /brand/. See `AdminBrandingPage.js` "Login Page Hero" section.
  login_hero_video_url: '',
  login_hero_poster_url: '',
};

function FeatureRow({ icon: Icon, title, description, tint }) {
  // In dark mode the parent panel is a true dark surface, so the typography
  // and icon pop better when tinted with the user's theme accent (gold by
  // default) rather than left as pure white. In light mode we keep the
  // original white-on-dark-gradient look that the original design ships.
  const onDark = !!tint;
  const headingColor = onDark ? tint : '#ffffff';
  const iconBgStyle = onDark
    ? { backgroundColor: `${tint}1f`, border: `1px solid ${tint}55` } // 12% fill, 33% border
    : { backgroundColor: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)' };

  return (
    <div className="flex items-start gap-5">
      <div
        className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
        style={iconBgStyle}
      >
        <Icon className="w-7 h-7" strokeWidth={1.6} style={{ color: headingColor }} />
      </div>
      <div>
        <h3
          data-testid={`auth-feature-title-${title.toLowerCase().replace(/\s+/g, '-')}`}
          className="text-lg md:text-xl font-bold uppercase tracking-wide leading-tight"
          style={{ color: headingColor }}
        >
          {title}
        </h3>
        <p
          className="mt-1.5 text-sm max-w-xs leading-relaxed"
          style={{ color: onDark ? `${tint}cc` : 'rgba(255,255,255,0.75)' }}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

function resolveAssetUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
}

export function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    gstin: '',
  });
  // License redemption state (signup-only)
  const [licenseCode, setLicenseCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null); // {valid,tier,email_on_file,email_hint,requires_email,message}
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [notifications, setNotifications] = useState([]);
  const [notifBellOpen, setNotifBellOpen] = useState(false);
  const notifFeed = useDismissibleFeed(
    notifications,
    'auth-notifications',
    (n, i) => n?.id || `notif-${i}`,
    // Pre-auth context — no server sync; localStorage-only is fine here.
  );
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  // Falls back to the letter avatar if the brand logo URL fails to load.
  const [logoBroken, setLogoBroken] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const resetLicense = () => {
    setLicenseCode('');
    setValidation(null);
    setFormData({ email: '', password: '', name: '', company_name: '', gstin: '' });
  };

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/site-config/public`)
      .then((r) => {
        if (!cancelled) setBranding({ ...DEFAULT_BRANDING, ...r.data });
      })
      .catch(() => {});
    axios
      .get(`${BACKEND_URL}/api/notifications/public`)
      .then((r) => {
        if (!cancelled) setNotifications(r.data?.items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLogin) {
      const result = await login(formData.email, formData.password);
      if (!result.success) toast.error(result.error);
      else toast.success('Login successful!');
      return;
    }

    // Signup = license redemption
    if (!validation?.valid) {
      toast.error('Please validate your license code first');
      return;
    }
    if (!formData.email || !formData.password || !formData.name || !formData.company_name) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/licenses/redeem`, {
        license_code: licenseCode.trim(),
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name.trim(),
        company_name: formData.company_name.trim(),
        gstin: formData.gstin.trim() || null,
      });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success(
        `Welcome! Your ${res.data.organization.license_tier.toUpperCase()} organization is ready.`
      );
      window.location.href = '/';
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Signup failed');
    }
  };

  const handleValidateLicense = async () => {
    const code = licenseCode.trim();
    if (!code) {
      toast.error('Please enter your license code');
      return;
    }
    setValidating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/licenses/validate`, {
        license_code: code,
      });
      setValidation(res.data);
      if (!res.data.valid) {
        toast.error(res.data.message || 'Invalid license');
      } else {
        toast.success(
          `${res.data.tier.toUpperCase()} license accepted — complete your signup`
        );
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Validation failed');
      setValidation(null);
    } finally {
      setValidating(false);
    }
  };

  const { isDark, toggle: toggleThemeMode } = useThemeMode();

  // Source of truth for the AuthPage palette: the SuperAdmin-configured
  // theme default for the active mode. The Login dashboard is shown
  // BEFORE the user authenticates, so we deliberately ignore per-user
  // preferences here — every visitor sees the same brand-controlled scheme.
  const baseHex = (isDark ? branding.theme_default_dark : branding.theme_default_light)
    || (isDark ? '#f59e0b' : '#1a1a2e');
  const colors = useMemo(() => deriveBrandColors(baseHex), [baseHex]);

  const bgUrl = resolveAssetUrl(
    (isDark && branding.background_image_dark_url) || branding.background_image_url,
  );
  const logoUrl = resolveAssetUrl(
    (isDark && branding.brand_logo_dark_url) || branding.brand_logo_url,
  );
  // localStorage-backed image cache. Bypasses the K8s/Cloudflare ingress
  // which strips our backend's `Cache-Control: max-age=86400, immutable`
  // and forces a re-fetch on every reload. While the byte cache populates
  // we fall back to the raw URL so the first-ever paint isn't blank.
  const cachedLogo = useCachedImage(logoUrl);
  const cachedBg = useCachedImage(bgUrl);
  const effectiveLogoSrc = cachedLogo || logoUrl;
  const effectiveBgSrc = cachedBg || bgUrl;
  const showLogo = !!logoUrl && !logoBroken;

  return (
    <div
      data-testid="auth-page"
      className="min-h-screen w-full flex flex-col md:flex-row"
    >
      {/* ===== LEFT PANEL (40%) — Hero panel with looping video background;
          falls back to the theme gradient when the video can't decode. ===== */}
      <section
        data-testid="auth-left-panel"
        className="relative md:w-2/5 flex items-center justify-center p-10 md:p-14 overflow-hidden"
        style={{
          background: isDark
            ? 'linear-gradient(160deg, #0f172a 0%, #020617 100%)'
            : `linear-gradient(160deg, ${colors.dark} 0%, ${colors.accent} 140%)`,
        }}
      >
        {/* Looping ambient video. SuperAdmin-swappable via the Site
            Branding page (`login_hero_video_url` / `login_hero_poster_url`);
            blank fields fall through to the shipped repo defaults so the
            login page always has a hero. `<video>` naturally renders
            nothing when the source is unreachable / codec-unsupported,
            so the gradient behind it (see `background:` on the parent
            <section>) serves as the final graceful fallback. Muted +
            playsInline are mandatory for iOS Safari autoplay. */}
        <video
          data-testid="auth-left-video-bg"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster={resolveAssetUrl(branding.login_hero_poster_url) || '/brand/auth-hero-poster.jpg'}
          aria-hidden="true"
          key={branding.login_hero_video_url || 'default-hero'}
        >
          <source
            src={resolveAssetUrl(branding.login_hero_video_url) || '/brand/auth-hero.mp4'}
            type="video/mp4"
          />
        </video>
        {/* Dark scrim so the FeatureRow copy stays legible over any frame
            of the video. Kept in the same DOM position as the previous
            radial glow so the layering order (gradient → video → scrim →
            content) is deterministic. */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'linear-gradient(180deg, rgba(2,6,23,0.55) 0%, rgba(2,6,23,0.75) 100%)',
          }}
        />
        {/* The RupiWise brand mark was removed from this panel per product
            spec — the SuperAdmin-linked `brand_logo_url` / `brand_logo_dark_url`
            fields are no longer read here. The three feature rows are now
            vertically centred inside the panel. */}
        <div className="relative z-10 w-full max-w-md flex flex-col justify-center gap-10 md:gap-12">
          <FeatureRow
            icon={TrendingUp}
            title={branding.feature_1_title}
            description={branding.feature_1_desc}
            tint={isDark ? colors.accent : undefined}
          />
          <FeatureRow
            icon={ShieldCheck}
            title={branding.feature_2_title}
            description={branding.feature_2_desc}
            tint={isDark ? colors.accent : undefined}
          />
          <FeatureRow
            icon={Settings2}
            title={branding.feature_3_title}
            description={branding.feature_3_desc}
            tint={isDark ? colors.accent : undefined}
          />
        </div>
      </section>

      {/* ===== RIGHT PANEL (60%) — Light teal with login card ===== */}
      <section
        data-testid="auth-right-panel"
        className="relative md:w-3/5 flex items-center justify-center p-6 md:p-14 overflow-hidden"
        style={{ background: colors.light }}
      >
        {/* Mode toggle + notification bell (top-right of right panel).
            The Sun/Moon button lets prospective customers preview both the
            light and dark SuperAdmin themes before authenticating. */}
        <div className="absolute top-5 right-5 md:top-7 md:right-7 z-30 flex items-center gap-2.5">
          {branding.demo_button_enabled !== false && (
            <button
              type="button"
              onClick={() => setShowDemoModal(true)}
              data-testid="explore-live-demo-btn"
              title="No Sign-up Required"
              className="group inline-flex items-center gap-2 h-11 px-4 rounded-full font-bold text-sm text-white shadow-md hover:shadow-lg hover:scale-[1.03] active:scale-[0.97] transition-all"
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow: '0 6px 16px -4px rgba(245,158,11,0.55)',
              }}
            >
              <Rocket className="w-4 h-4" strokeWidth={2.4} />
              <span className="hidden sm:inline">Explore Live Demo</span>
              <span className="sm:hidden">Demo</span>
              <span
                aria-hidden
                className="hidden md:inline-block opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-semibold uppercase tracking-wider bg-white/25 px-1.5 py-0.5 rounded-full ml-1"
              >
                No Sign-up
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={toggleThemeMode}
            data-testid="auth-mode-toggle"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-11 h-11 rounded-full bg-white shadow-md border border-black/5 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
            style={{ color: colors.dark }}
          >
            {/* The icon shows the DESTINATION mode, not the current one,
                so users see "tap to go to the moon (dark)" when in light. */}
            {isDark
              ? <Sun className="w-5 h-5" strokeWidth={2} />
              : <Moon className="w-5 h-5" strokeWidth={2} />}
          </button>
          <Popover
            open={notifBellOpen}
            onOpenChange={(o) => {
              if (o) notifFeed.markAllAsViewed();
              setNotifBellOpen(o);
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="auth-notification-bell"
                aria-label="Notifications"
                className="relative w-11 h-11 rounded-full bg-white shadow-md border border-black/5 flex items-center justify-center hover:scale-105 transition-transform"
                style={{ color: colors.dark }}
              >
                <Bell className="w-5 h-5" />
                {notifFeed.unreadCount > 0 && (
                  <span
                    data-testid="auth-notification-badge"
                    className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold text-white flex items-center justify-center"
                    style={{ background: colors.accent }}
                  >
                    {notifFeed.unreadCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="w-[22rem] p-0 border-slate-200 shadow-xl"
              data-testid="auth-notification-popover"
            >
              <div
                className="px-4 py-3 border-b flex items-center justify-between"
                style={{ background: colors.dark, color: '#fff' }}
              >
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  <span className="font-semibold text-sm">Notifications</span>
                </div>
                <span className="text-[11px] uppercase tracking-wider opacity-75">
                  {notifFeed.visibleItems.length} active
                </span>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {notifFeed.visibleItems.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">
                    You're all caught up — no notifications right now.
                  </div>
                ) : (
                  <ul>
                    {notifFeed.visibleItems.map((n, idx) => {
                      const isOffer = n.type === 'offer';
                      const TypeIcon = isOffer ? Sparkles : Megaphone;
                      const id = n.id || `notif-${idx}`;
                      return (
                        <li
                          key={id}
                          data-testid={`auth-notification-item-${id}`}
                          className="group relative px-4 py-3 border-b last:border-b-0 flex gap-3 items-start hover:bg-slate-50"
                        >
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{
                              background: isOffer ? '#fef3c7' : '#dbeafe',
                              color: isOffer ? '#b45309' : '#1d4ed8',
                            }}
                          >
                            <TypeIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0 pr-5">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h4 className="text-sm font-semibold text-slate-900 truncate">
                                {n.title}
                              </h4>
                              <span
                                className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold ${
                                  isOffer
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'chip-theme-primary'
                                }`}
                              >
                                {n.type}
                              </span>
                            </div>
                            <p className="text-xs text-slate-600 leading-snug">{n.message}</p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); notifFeed.dismissOne(id); }}
                            aria-label="Dismiss notification"
                            title="Dismiss"
                            data-testid={`auth-notification-dismiss-${id}`}
                            className="absolute top-2 right-2 h-5 w-5 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" strokeWidth={2.5} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Background image — constrained to the area behind/around the login
            card (right-bottom of the right panel), per design spec. Opacity
            is mode-aware and tunable by the SuperAdmin (Brand Assets section). */}
        {bgUrl && (
          <div
            aria-hidden
            data-testid="auth-bg-image"
            className="absolute bg-center bg-cover pointer-events-none"
            style={{
              backgroundImage: `url(${bgUrl})`,
              top: '22%',
              left: '24%',
              right: '0',
              bottom: '0',
              opacity:
                Math.max(
                  0,
                  Math.min(
                    100,
                    Number(
                      (isDark
                        ? branding.background_opacity_dark
                        : branding.background_opacity) ?? 30,
                    ),
                  ),
                ) / 100,
            }}
          />
        )}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: `linear-gradient(180deg, transparent 0%, ${colors.light} 100%)` }}
        />

        {/* Login card — fixed square aspect for signup (more fields), auto-fit for sign-in */}
        <div
          data-testid="auth-card"
          className={`relative z-10 w-full max-w-[480px] bg-white rounded-2xl shadow-xl border border-black/5 flex flex-col p-8 md:p-10 ${
            isLogin ? '' : 'md:aspect-square overflow-y-auto'
          }`}
        >
          <div className="flex flex-col items-center text-center mb-6">
            {showLogo ? (
              <img
                src={effectiveLogoSrc}
                alt=""
                data-testid="auth-brand-logo"
                onError={() => setLogoBroken(true)}
                className="max-h-20 md:max-h-24 w-auto object-contain mb-3"
              />
            ) : (
              <div
                className="w-14 h-14 rounded-full mb-3 flex items-center justify-center"
                style={{ background: colors.accent, color: '#fff' }}
              >
                <span className="text-xl font-bold">B</span>
              </div>
            )}
            <p className="text-slate-500 text-sm mt-1.5">
              {isLogin ? branding.login_subtitle_text : 'Create your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* ===== SIGNUP STEP 1: License code entry ===== */}
            {!isLogin && !validation?.valid && (
              <div
                data-testid="auth-license-step"
                className="space-y-3 rounded-xl border border-dashed p-4"
                style={{ borderColor: `${colors.accent}55`, background: `${colors.accent}0a` }}
              >
                <div className="flex items-start gap-2">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: colors.accent, color: '#fff' }}
                  >
                    <span className="text-xs font-bold">1</span>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">Enter your license code</div>
                    <div className="text-xs text-slate-500">
                      Sent to your email after purchase (Amazon / Flipkart / Website / Offline).
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="auth-license-input"
                    value={licenseCode}
                    onChange={(e) => setLicenseCode(e.target.value.toUpperCase())}
                    placeholder="e.g. PRO-A1B2-C3D4-E5F6"
                    className="h-11 font-mono text-sm tracking-wider"
                  />
                  <Button
                    type="button"
                    onClick={handleValidateLicense}
                    disabled={validating}
                    data-testid="auth-license-validate-btn"
                    className="h-11 px-4 font-semibold text-white"
                    style={{ backgroundColor: colors.accent }}
                  >
                    {validating ? 'Checking…' : 'Validate'}
                  </Button>
                </div>
                {validation && !validation.valid && (
                  <p data-testid="auth-license-error" className="text-xs text-red-600">
                    {validation.message || 'Invalid license.'}
                  </p>
                )}
              </div>
            )}

            {/* ===== SIGNUP STEP 2 + LOGIN: regular fields ===== */}
            {(isLogin || validation?.valid) && (
              <>
                {!isLogin && validation?.valid && (
                  <div
                    data-testid="auth-license-summary"
                    className="flex items-center justify-between rounded-lg px-3 py-2 text-xs"
                    style={{ background: `${colors.accent}14`, color: colors.dark }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold uppercase tracking-wider">
                        {validation.tier} LICENSE
                      </span>
                      <span className="text-slate-500">·</span>
                      <span>{validation.tier === 'pro' ? '10 seats' : '3 seats'}</span>
                      {validation.email_on_file && (
                        <>
                          <span className="text-slate-500">·</span>
                          <span>Issued to {validation.email_hint}</span>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={resetLicense}
                      className="text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline"
                    >
                      Change
                    </button>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-slate-700 text-sm">
                    Email{' '}
                    {!isLogin && validation?.email_on_file && (
                      <span className="text-[10px] text-slate-500 normal-case">
                        (must match {validation.email_hint})
                      </span>
                    )}
                    {!isLogin && validation?.requires_email && (
                      <span className="text-[10px] text-amber-600 normal-case">
                        (no email on license — we'll add yours)
                      </span>
                    )}
                  </Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-11"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700 text-sm">Password</Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs font-medium underline-offset-2 hover:underline"
                        style={{ color: colors.accent }}
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={isLogin ? undefined : 6}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      title={showPassword ? 'Hide password' : 'Show password'}
                      data-testid="toggle-password-visibility"
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 rounded-r-md"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {!isLogin && (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-slate-700 text-sm">Full Name</Label>
                      <Input
                        id="name"
                        data-testid="name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="company" className="text-slate-700 text-sm">Company Name</Label>
                      <Input
                        id="company"
                        data-testid="company-input"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        required
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="gstin" className="text-slate-700 text-sm">GSTIN (Optional)</Label>
                      <Input
                        id="gstin"
                        data-testid="gstin-input"
                        value={formData.gstin}
                        onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                        className="h-11"
                      />
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  data-testid="auth-submit-btn"
                  className="w-full h-12 font-semibold text-white mt-2"
                  style={{ backgroundColor: colors.dark }}
                >
                  {isLogin
                    ? 'Sign In'
                    : `Create ${validation?.tier?.toUpperCase() || ''} Organization`}
                </Button>
              </>
            )}
          </form>

          <div className="text-center mt-5 text-sm text-slate-600">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                resetLicense();
              }}
              data-testid="toggle-auth-btn"
              className="font-semibold underline-offset-2 hover:underline"
              style={{ color: colors.accent }}
            >
              {isLogin ? 'Redeem a license' : 'Sign in'}
            </button>
          </div>
        </div>

        {/* Fixed Made in India badge (never theming-affected, never editable) */}
        <div
          data-testid="made-in-india-badge"
          className="absolute bottom-5 right-5 md:bottom-7 md:right-7 z-20 flex items-center gap-2 pointer-events-none select-none"
        >
          <img
            src="/brand/made-in-india.png"
            alt="Made in India, For India"
            className="w-14 h-14 md:w-16 md:h-16 object-contain drop-shadow-md"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span
              className="text-[10px] font-bold tracking-widest"
              style={{ color: isDark ? colors.accent : '#1e293b' /* slate-800 */ }}
            >
              MADE IN INDIA
            </span>
            <span
              className="text-[10px] font-semibold tracking-wider"
              style={{ color: isDark ? `${colors.accent}cc` : '#475569' /* slate-600 */ }}
            >
              FOR INDIA
            </span>
          </div>
        </div>
      </section>

      <ForgotPasswordDialog
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
        initialEmail={formData.email}
        accentColor={colors.dark || colors.accent}
      />
      <DemoSandboxModal
        open={showDemoModal}
        onOpenChange={setShowDemoModal}
        accentColor={colors.accent}
        goldColor="#f59e0b"
      />
    </div>
  );
}
